import { Component } from '@theme/component';

/**
 * @typedef {Object} AxArticleCarouselRefs
 * @property {HTMLElement} [viewport] - Wrapper whose height is reserved during the swap.
 * @property {HTMLElement & { swiper?: any, initialize?: () => void }} [swiper] - <swiper-container init="false">.
 * @property {HTMLElement} [fallback] - Server-rendered slides, moved into Swiper on init.
 * @property {HTMLElement} [arrows] - Arrows wrapper (hidden when every slide fits).
 * @property {HTMLButtonElement} [previous] - Previous arrow (snippet slideshow-arrow).
 * @property {HTMLButtonElement} [next] - Next arrow (snippet slideshow-arrow).
 */

/**
 * Product carousel inserted in an article body by a `[[ax-cN]]` code
 * (snippets/ax-article-carousel.liquid).
 *
 * Same pipeline as ax-sliders-products / ax-tabbed-base : Swiper `init="false"`, the
 * server-rendered slides are moved into <swiper-slide> elements, the height is reserved
 * during the swap so the article text below does not jump.
 *
 * @extends {Component<AxArticleCarouselRefs>}
 */
class AxArticleCarousel extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  /** Arrow handler (on:click="/previous" in slideshow-arrow). */
  previous() {
    this.#swiper?.slidePrev();
  }

  /** Arrow handler (on:click="/next" in slideshow-arrow). */
  next() {
    this.#swiper?.slideNext();
  }

  async #init() {
    await customElements.whenDefined('swiper-container');

    const { viewport, swiper: swiperEl, fallback } = this.refs;
    if (!viewport || !swiperEl || !fallback || this.#swiper) return;

    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    const reservedHeight = viewport.getBoundingClientRect().height;
    if (reservedHeight > 0) viewport.style.minHeight = `${reservedHeight}px`;

    for (const slideElement of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideElement);
      swiperEl.appendChild(swiperSlide);
    }

    fallback.remove();

    const gap = parseInt(swiperEl.dataset.gap ?? '0', 10) || 0;

    Object.assign(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: gap,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      // Pas de glisser depuis un contrôle de carte. Chrome donne le focus au bouton dès
      // l'appui : Swiper (onTouchMove) prend alors le moindre déplacement d'un pixel pour un
      // glisser et annule le clic, donc l'ajout au panier ("il faut cliquer deux fois").
      noSwipingSelector: 'button, input, label, select, summary, .ax-variant-card__options',
      // Flèches gauche/droite seulement : Page préc./suiv. doivent rester au défilement de la
      // lecture, même quand un carrousel est à l'écran.
      keyboard: { enabled: true, pageUpDown: false },
      a11y: {
        enabled: true,
        slideLabelMessage: this.#slideLabel(),
      },
    });

    swiperEl.initialize?.();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      viewport.style.minHeight = '';
    });

    this.#swiper = swiperEl.swiper;
    if (!this.#swiper) return;

    this.#syncArrows();
    for (const event of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize', 'lock', 'unlock']) {
      this.#swiper.on(event, this.#syncArrows);
    }

    // The cards' media were outside the viewport tree while in the fallback : make the
    // lazy observer (observer-CFH) and lazy-skeleton see them in their new place.
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  /**
   * Swiper slide label from the theme translation ("Diapositive %index% sur %total%").
   * @returns {string}
   */
  #slideLabel() {
    const label = this.dataset.slideLabel || '';
    return label.replace('%index%', '{{index}}').replace('%total%', '{{slidesLength}}');
  }

  /**
   * Disabled state of the arrows, and no arrows at all when every slide fits. An arrow that
   * gets disabled while focused hands the focus to the other one, instead of dropping it
   * on <body>.
   */
  #syncArrows = () => {
    const swiper = this.#swiper;
    if (!swiper) return;

    const { arrows, previous, next } = this.refs;
    const atStart = Boolean(swiper.isBeginning);
    const atEnd = Boolean(swiper.isEnd);
    const focused = document.activeElement;

    if (atEnd && !atStart && focused === next) previous?.focus();
    if (atStart && !atEnd && focused === previous) next?.focus();

    if (arrows) arrows.hidden = Boolean(swiper.isLocked);
    if (previous) previous.disabled = atStart;
    if (next) next.disabled = atEnd;
  };

  #destroy() {
    if (!this.#swiper) return;

    try {
      this.#swiper.destroy(true, true);
    } catch {
      // Swiper can throw during teardown if already destroyed.
    }
    this.#swiper = null;
  }
}

if (!customElements.get('ax-article-carousel')) {
  customElements.define('ax-article-carousel', AxArticleCarousel);
}
